import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React from "react"
import { FormProvider, type UseFormProps, useForm } from "react-hook-form"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker } from "@/components/UiKit"
import type { ChangeLogQueryableFields, DateRange } from "@/graphql/codegen/graphql"
import ChangeLogCategoryFilter from "@/screens/ChangeLog/components/filter/ChangeLogCategoryFilter"
import ChangeLogSubCategoryFilter from "@/screens/ChangeLog/components/filter/ChangeLogSubCategoryFilter"
import { ChangeLogUserFilter } from "@/screens/ChangeLog/components/filter/ChangeLogUserFilter"
import type { ChangeLogSearchFilters } from "@/screens/ChangeLog/stores/useChangeLogSearchStore"

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

export type FormValues = ChangeLogQueryableFields

export const FilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const navigate = useNavigate({ from: "/app/admin/change-log" })
  const currentSearch: ChangeLogSearchFilters = useSearch({
    from: "/app/admin/change-log/",
  })

  const { start, end, userIds, categories, subcategories } = currentSearch

  const methods = useForm<FormValues>({
    values: {
      userIds,
      categories,
      subcategories,
    },
    mode: "onChange",
  } as UseFormProps<FormValues>)

  const handleClearFilters = async () => {
    methods.reset()
    await navigate({
      search: (prev: Record<string, any>) => ({
        ...prev,
        userIds: undefined,
        categories: undefined,
        subcategories: undefined,
        start: undefined,
        end: undefined,
      }),
      replace: true,
    })
  }

  const handleFilterChange = async (name: keyof ChangeLogSearchFilters | keyof DateRange, value: unknown) => {
    await navigate({
      search: (prev: Record<string, unknown>) => {
        const val = Array.isArray(value) && value.length === 0 ? undefined : value
        return {
          ...prev,
          [name]: val || undefined,
        }
      },
      replace: true,
    })
  }

  return (
    <SearchFilterDrawer
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      open={showFilterDrawer}
      clear={handleClearFilters}
    >
      <FormProvider {...methods}>
        <Box className='h-full min-w-80'>
          <Box className='flex h-1/4 flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Category
            </Typography>
            <Divider />

            <ChangeLogCategoryFilter onFilterChange={handleFilterChange} />
          </Box>
          <Box className='flex h-1/4 flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Sub-Category
            </Typography>
            <Divider />

            <ChangeLogSubCategoryFilter onFilterChange={handleFilterChange} />
          </Box>

          <Box className='flex h-1/6 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Date Range
            </Typography>
            <Divider />

            <Box className='mt-4 flex gap-x-2 px-4'>
              <Box>
                <AerosDatePicker
                  label='From'
                  required
                  name='startDate'
                  value={start ?? ""}
                  onChange={(date) => handleFilterChange("start", date)}
                />
              </Box>
              <Box>
                <AerosDatePicker
                  label='To'
                  name='endDate'
                  value={end ?? ""}
                  onChange={(date) => handleFilterChange("end", date)}
                />
              </Box>
            </Box>
          </Box>

          <Box className='flex h-1/3 flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Users Name
            </Typography>
            <Divider />

            <ChangeLogUserFilter onFilterChange={handleFilterChange} />
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
